<script>
  import { onMount } from 'svelte';
  import PromotionCard from '../components/PromotionCard.svelte';
  import { getApiUrl } from '../api';
  import PaymentMethodSelectorModal from '../components/PaymentMethodSelectorModal.svelte';
  import CategorySelectorModal from '../components/CategorySelectorModal.svelte';
  import StoreSelectorModal from '../components/StoreSelectorModal.svelte'; 
  import CreditCardSelectorModal from '../components/AdvancedCreditCardSelectorModal.svelte'; 
  import PlaceSelectorModal from '../components/PlaceSelectorModal.svelte'; // New Import
  import { selectedCountry } from '../countryStore';
  import { t } from '../i18nStore';

  // --- Search Input State ---
  let nameQuery = "";
  let bankQuery = "";
  let spendingQuery = "";
  let isOnline = false;
  let isInStore = false;
  let includeUpcoming = false;
  let includeExpired = false;

  let selectedMethods = [];
  let selectedCategories = [];
  let selectedStores = [];
  let selectedCreditCards = [];
  let selectedPlaces = []; // New State
  let locationAware = false;
  let userLat = null;
  let userLng = null;
  let locationLoading = false;
  let locationError = "";

  // --- Component State ---
  let searchResults = [];
  let isLoading = false;
  let error = "";
  let searchAttempted = false;
  
  // Modal Visibility
  let showPaymentMethodModal = false;
  let showCategoryModal = false;
  let showStoreModal = false;
  let showCreditCardModal = false;
  let showPlaceModal = false; // New Modal State
  
  // Pagination
  let page = 1;
  const pageSize = 10;
  let hasMore = false;
  let isSearchExpanded = true;
  
  onMount(() => {
    // Persistence: Load from Session Storage
    const savedState = sessionStorage.getItem('promoSearchState');
    if (savedState) {
        const state = JSON.parse(savedState);
        nameQuery = state.nameQuery || "";
        bankQuery = state.bankQuery || "";
        spendingQuery = state.spendingQuery || "";
        isOnline = state.isOnline || false;
        isInStore = state.isInStore || false;
        includeUpcoming = state.includeUpcoming || false;
        includeExpired = state.includeExpired || false;
        selectedMethods = state.selectedMethods || [];
        selectedCategories = state.selectedCategories || [];
        selectedStores = state.selectedStores || [];
        selectedCreditCards = state.selectedCreditCards || [];
        selectedPlaces = state.selectedPlaces || []; // Load places
        locationAware = state.locationAware || false;
        userLat = state.userLat || null;
        userLng = state.userLng || null;
        
        handleSearch(true, false); 
    }
  });

  function toggleSearch() { isSearchExpanded = !isSearchExpanded; }

  function toggleLocationAware() {
    if (!locationAware) {
      if (!navigator.geolocation) {
        locationError = $t('geo.not_supported');
        return;
      }
      locationLoading = true;
      locationError = "";
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          userLat = pos.coords.latitude;
          userLng = pos.coords.longitude;
          locationAware = true;
          locationLoading = false;
        },
        (err) => {
          console.warn("Geolocation error:", err);
          locationError = $t('geo.permission_denied');
          locationAware = false;
          locationLoading = false;
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      locationAware = false;
      userLat = null;
      userLng = null;
      locationError = "";
    }
  }

  async function handleSearch(reset = false, saveState = true) {
    if (reset) {
        page = 1;
        searchResults = [];
        hasMore = false;
        searchAttempted = true;
    }

    // Validate inputs
    const hasCriteria = nameQuery || bankQuery || spendingQuery || isOnline || isInStore || includeUpcoming || includeExpired ||
                        selectedMethods.length > 0 || selectedCategories.length > 0 || 
                        selectedStores.length > 0 || selectedCreditCards.length > 0 || selectedPlaces.length > 0 || locationAware;

    if (!hasCriteria) {
       error = $t('search.enter_criteria');
       return;
    }

    isLoading = true;
    error = "";

    if (saveState) {
        const state = {
            nameQuery, bankQuery, spendingQuery, isOnline, isInStore, includeUpcoming, includeExpired,
            selectedMethods, selectedCategories, selectedStores, selectedCreditCards, selectedPlaces,
            locationAware, userLat, userLng
        };
        sessionStorage.setItem('promoSearchState', JSON.stringify(state));
    }

    const params = new URLSearchParams();
    if (nameQuery) params.append("name", nameQuery);
    if (bankQuery) params.append("bank", bankQuery);
    if (spendingQuery) params.append("spending", spendingQuery);
    if (isOnline) params.append("isOnline", "true");
    if (isInStore) params.append("isInStore", "true");
    if (includeUpcoming) params.append("includeUpcoming", "true");
    if (includeExpired) params.append("includeExpired", "true");

    selectedMethods.forEach(method => params.append("methods", method));
    selectedCategories.forEach(category => params.append("categories", category));
    selectedStores.forEach(store => params.append("stores", store));
    selectedCreditCards.forEach(card => params.append("creditCards", card));
    selectedPlaces.forEach(place => params.append("places", place)); // Add places to params
    
    if (locationAware && userLat != null && userLng != null) {
      params.append("locationAware", "true");
      params.append("lat", userLat.toString());
      params.append("lng", userLng.toString());
      params.append("radius", "1000");
    }

    const localDate = new Date();
    const currentDate = `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, '0')}-${String(localDate.getDate()).padStart(2, '0')}`;
    params.append("currentDate", currentDate);
    params.append("countryCode", $selectedCountry);
    params.append("page", page);
    params.append("pageSize", pageSize);
    
    const searchURL = getApiUrl(`/promotions/search?${params.toString()}`);

    try {
      const response = await fetch(searchURL);
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Search failed.");
      }
      const result = await response.json();
      const newPromos = result.data || [];
      
      if (reset) {
          searchResults = newPromos;
          if (window.innerWidth < 768) isSearchExpanded = false;
      } else {
          searchResults = [...searchResults, ...newPromos];
      }
      
      hasMore = page < result.totalPages;
      
    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  }

  function handleReset() {
    nameQuery = "";
    bankQuery = "";
    spendingQuery = "";
    isOnline = false;
    isInStore = false;
    includeUpcoming = false;
    includeExpired = false;
    selectedMethods = [];
    selectedCategories = [];
    selectedStores = [];
    selectedCreditCards = [];
    selectedPlaces = [];
    locationAware = false;
    userLat = null;
    userLng = null;
    locationError = "";
    locationLoading = false;
    
    sessionStorage.removeItem('promoSearchState');
    searchResults = [];
    searchAttempted = false;
    error = "";
    page = 1;
  }

  function loadMore() { page += 1; handleSearch(false); }
  
  // Modal Confirm Handlers
  function handlePaymentMethodConfirm(event) { selectedMethods = event.detail.selected; showPaymentMethodModal = false; }
  function handleCategoryConfirm(event) { selectedCategories = event.detail.selected; showCategoryModal = false; }
  function handleStoreConfirm(event) { selectedStores = event.detail.selected; showStoreModal = false; }
  function handleCreditCardConfirm(event) { selectedCreditCards = event.detail.selected; showCreditCardModal = false; }
  function handlePlaceConfirm(event) { selectedPlaces = event.detail.selected; showPlaceModal = false; }
</script>

<!-- Modals -->
<PaymentMethodSelectorModal bind:showModal={showPaymentMethodModal} preSelectedMethods={selectedMethods} on:confirm={handlePaymentMethodConfirm} on:close={() => showPaymentMethodModal = false} />
<CategorySelectorModal bind:showModal={showCategoryModal} preSelectedCategories={selectedCategories} on:confirm={handleCategoryConfirm} on:close={() => showCategoryModal = false} />
<StoreSelectorModal bind:showModal={showStoreModal} preSelectedStores={selectedStores} on:confirm={handleStoreConfirm} on:close={() => showStoreModal = false} />
<CreditCardSelectorModal bind:showModal={showCreditCardModal} preSelectedCards={selectedCreditCards} on:confirm={handleCreditCardConfirm} on:close={() => showCreditCardModal = false} />
<PlaceSelectorModal bind:showModal={showPlaceModal} preSelectedPlaces={selectedPlaces} on:confirm={handlePlaceConfirm} on:close={() => showPlaceModal = false} />

<div class="search-container">
  <div class="page-header">
      <h2>{$t('search.title')} ({$selectedCountry})</h2>
       <button class="toggle-search-btn" on:click={toggleSearch}>
        <!-- Localized Toggle Text -->
        {isSearchExpanded ? $t('search.hide_filters') + ' ▲' : $t('search.show_filters') + ' ▼'}
      </button>
  </div>
  
  {#if isSearchExpanded}
  <form on:submit|preventDefault={() => handleSearch(true)} class="search-form">
    <div class="form-grid">
      <!-- Text Inputs -->
      <div class="form-group">
        <label for="name">{$t('search.keyword')}</label>
        <input type="text" id="name" bind:value={nameQuery} placeholder="e.g., dining, 50% off" />
      </div>
      <div class="form-group">
        <label for="bank">{$t('search.bank')}</label>
        <input type="text" id="bank" bind:value={bankQuery} placeholder="e.g., HSBC" />
      </div>
      <div class="form-group">
        <label for="spending">{$t('search.spending')}</label>
        <input type="number" step="0.01" min="0" id="spending" bind:value={spendingQuery} placeholder="e.g., 1000" />
      </div>

      <!-- Checkbox Group -->
      <div class="form-group checkbox-group">
        <label>{$t('search.options')}</label>
        <div class="checkbox-container">
           <label class="check-label">
             <input type="checkbox" bind:checked={isOnline} /> 
             <span>{$t('search.online')}</span>
           </label>
           <label class="check-label">
             <input type="checkbox" bind:checked={isInStore} /> 
             <span>{$t('search.instore')}</span>
           </label>
           <label class="check-label highlight">
             <input type="checkbox" bind:checked={includeUpcoming} /> 
             <span>{$t('search.upcoming')}</span>
           </label>
           <label class="check-label expired-check">
             <input type="checkbox" bind:checked={includeExpired} /> 
             <span>{$t('search.expired')}</span>
           </label>
        </div>

        <!-- Location-Aware Toggle (Beta) -->
        <div class="location-toggle-box">
          <label class="toggle-container">
            <input type="checkbox" checked={locationAware} on:change={toggleLocationAware} disabled={locationLoading} />
            <span class="toggle-slider"></span>
            <span class="toggle-text">
              {$t('geo.location_aware')}
              <span class="beta-tag">{$t('geo.beta')}</span>
            </span>
          </label>
          {#if locationLoading}
            <span class="loc-badge loading">⏳ {$t('geo.locating')}</span>
          {:else if locationAware && userLat != null}
            <span class="loc-badge active">📍 {$t('geo.location_ready')}</span>
          {/if}
          {#if locationError}
            <div class="loc-error-msg">⚠️ {locationError}</div>
          {/if}
        </div>
      </div>
      
      <!-- Modal Selectors -->
      <div class="form-group">
        <label>{$t('search.categories')}</label>
        <button type="button" class="select-btn" on:click={() => showCategoryModal = true}>
             {selectedCategories.length > 0 ? `${selectedCategories.length} ${$t('search.selected')}` : $t('search.select_categories')}
        </button>
      </div>
      <div class="form-group">
        <label>{$t('search.methods')}</label>
        <button type="button" class="select-btn" on:click={() => showPaymentMethodModal = true}>
            {selectedMethods.length > 0 ? `${selectedMethods.length} ${$t('search.selected')}` : $t('search.select_methods')}
        </button>
      </div>
      
      <!-- Stores & Places -->
      <div class="form-group">
        <label>{$t('search.stores')}</label>
        <button type="button" class="select-btn" on:click={() => showStoreModal = true}>
            {selectedStores.length > 0 ? `${selectedStores.length} ${$t('search.selected')}` : $t('search.select_stores')}
        </button>
      </div>
      <div class="form-group">
        <label>{$t('search.places')}</label>
        <button type="button" class="select-btn" on:click={() => showPlaceModal = true}>
            {selectedPlaces.length > 0 ? `${selectedPlaces.length} ${$t('search.selected')}` : $t('search.select_places')}
        </button>
      </div>
      
      <div class="form-group">
        <label>{$t('search.credit_cards')}</label>
        <button type="button" class="select-btn" on:click={() => showCreditCardModal = true}>
            {selectedCreditCards.length > 0 ? `${selectedCreditCards.length} ${$t('search.selected')}` : $t('search.select_cards')}
        </button>
      </div>
    </div>
    
    <div class="btn-row">
        <button type="button" class="reset-btn" on:click={handleReset} disabled={isLoading}>{$t('search.reset')}</button>
        <button type="submit" class="search-btn" disabled={isLoading}>
          {#if isLoading}{$t('search.searching')}{:else}{$t('search.btn')}{/if}
        </button>
    </div>
  </form>
  {/if}
  
  {#if error} <p class="error">{error}</p> {/if}

  <div class="results-container">
    {#if searchResults.length > 0}
      <h3>{$t('nav.search')} ({searchResults.length})</h3>
      <div class="promo-list">
        {#each searchResults as promo (promo._id)}
          <PromotionCard {promo} />
        {/each}
      </div>
      
      {#if hasMore}
        <div class="load-more-container">
            <button class="load-more-btn" on:click={loadMore} disabled={isLoading}>
                {isLoading ? $t('search.searching') : $t('search.load_more')}
            </button>
        </div>
      {/if}
    {:else if searchAttempted && !isLoading}
      <p>{$t('search.no_results')} {$selectedCountry}.</p>
    {:else if isLoading && searchResults.length === 0}
        <p class="loading">{$t('search.searching')}</p>
    {/if}
  </div>
</div>

<style>
  .search-container { max-width: 1200px; margin: 0 auto 2rem; padding: 0; }
  .page-header, .search-form, .results-container, p { padding-left: 1rem; padding-right: 1rem; }
  .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; margin-top: 1rem; }
  .toggle-search-btn { background: none; border: 1px solid #ccc; color: #555; padding: 0.4rem 0.8rem; border-radius: 4px; font-size: 0.9rem; }
  h2 { margin: 0; font-size: 1.5rem; }

  .search-form { padding: 1.5rem; background: #fff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); margin-bottom: 2rem; border: 1px solid #eee; margin-left: 1rem; margin-right: 1rem; }
  .form-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
  @media (min-width: 768px) { .form-grid { grid-template-columns: repeat(3, 1fr); } }

  .form-group { text-align: left; }
  label { display: block; margin-bottom: 0.4rem; font-weight: 500; font-size: 0.9rem; }
  input { width: 100%; padding: 0.6rem; border: 1px solid #ccc; border-radius: 4px; font-size: 1rem; box-sizing: border-box; }
  
  .checkbox-container {
    display: flex;
    flex-wrap: wrap; 
    gap: 1rem;
    align-items: center;
    padding: 0.4rem 0; 
    min-height: 42px; 
  }

  .check-label { 
    display: flex; 
    align-items: center; 
    gap: 0.4rem; 
    font-weight: normal; 
    margin: 0; 
    cursor: pointer;
    white-space: nowrap; 
  }
  
  input[type="checkbox"] {
    width: 1.2rem;
    height: 1.2rem;
    margin: 0;
    cursor: pointer;
  }
  
  .highlight { color: #007bff; font-weight: 500; }
  
  .select-btn {
    width: 100%; background-color: #6c757d; color: white; border: none; border-radius: 4px;
    font-size: 0.9rem; padding: 0.6rem; cursor: pointer;
  }
  
  .btn-row {
      display: flex;
      gap: 1rem;
  }
  
  .search-btn { width: 100%; padding: 0.75rem; background-color: #28a745; color: white; border: none; border-radius: 4px; font-size: 1.1rem; cursor: pointer; }
  
  .reset-btn { width: 100px; padding: 0.75rem; background-color: #f8f9fa; color: #333; border: 1px solid #ccc; border-radius: 4px; font-size: 1.1rem; cursor: pointer; }
  .reset-btn:hover { background-color: #e2e6ea; }

  button:disabled { background-color: #aaa; }
  .error { color: red; font-weight: bold; text-align: center; }
  .results-container h3 { text-align: center; }
  .promo-list { display: grid; grid-template-columns: 1fr; gap: 1.5rem; }
  @media (min-width: 600px) { .promo-list { grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); } }
  .loading { text-align: center; color: #666; }
  .load-more-container { text-align: center; margin-top: 2rem; }
  .load-more-btn { background-color: #f8f9fa; color: #333; border: 1px solid #ccc; padding: 0.75rem 2rem; font-size: 1rem; cursor: pointer; transition: background 0.2s; border-radius: 4px; }
  .load-more-btn:hover { background-color: #e2e6ea; }

  .location-toggle-box {
    margin-top: 0.6rem;
    padding-top: 0.5rem;
    border-top: 1px dashed #ddd;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.6rem;
  }
  .toggle-container {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    user-select: none;
  }
  .toggle-container input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }
  .toggle-slider {
    position: relative;
    display: inline-block;
    width: 36px;
    height: 20px;
    background-color: #ccc;
    border-radius: 20px;
    transition: 0.3s;
  }
  .toggle-slider:before {
    position: absolute;
    content: "";
    height: 14px;
    width: 14px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    border-radius: 50%;
    transition: 0.3s;
  }
  .toggle-container input:checked + .toggle-slider {
    background-color: #2563eb;
  }
  .toggle-container input:checked + .toggle-slider:before {
    transform: translateX(16px);
  }
  .toggle-text {
    font-size: 0.9rem;
    font-weight: 600;
    color: #1e293b;
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
  }
  .beta-tag {
    background: #e0e7ff;
    color: #3730a3;
    font-size: 0.7rem;
    padding: 1px 6px;
    border-radius: 8px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .loc-badge {
    font-size: 0.75rem;
    padding: 2px 8px;
    border-radius: 12px;
    font-weight: 500;
  }
  .loc-badge.active {
    background: #dcfce7;
    color: #166534;
  }
  .loc-badge.loading {
    background: #fef3c7;
    color: #92400e;
  }
  .loc-error-msg {
    width: 100%;
    font-size: 0.8rem;
    color: #dc2626;
    margin-top: 2px;
  }
</style>